import type { FC, ReactNode } from 'react'

import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { AsciiField } from '@/components/generals/ascii-field'
import { siteConfig } from '@/constants/site'

const TITLE = 'Contact Native | Start a Website, App or AI Product Project'
const DESCRIPTION = 'Brief us on your project. We reply within 24 hours, and if it is a fit we set up a kickoff call the same week.'

const STEPS = [
    { title: 'Brief review', text: 'We read your brief within 24 hours and reply.' },
    { title: '30-min call', text: "If it's a fit, we book a kickoff this week." },
    { title: 'Scope + timeline', text: "We send a clear plan. You decide if it's right." },
    { title: 'Build begins', text: 'Depending on the project, you can have v1 ready within a week.' }
]

const Contact: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title={TITLE}
                description={DESCRIPTION}
                path='/contact'
                jsonLd={[{
                    '@type': 'ContactPage',
                    '@id': `${siteConfig.url}/contact#webpage`,
                    url: `${siteConfig.url}/contact`,
                    name: TITLE,
                    description: DESCRIPTION,
                    isPartOf: { '@id': `${siteConfig.url}/#website` },
                    primaryImageOfPage: { '@type': 'ImageObject', url: siteConfig.ogImage, width: 1200, height: 630 },
                    inLanguage: 'en'
                }]}
            />

            <section className='brief'>
                <AsciiField className='brief-ascii-left' seed={7} mask='band' cell={6.4} power={1.5} gain={2.1} />
                <AsciiField className='brief-ascii-right' seed={23} mask='c' cell={12} power={1.5} gain={2.1} />
                <div className='brief-rail' aria-hidden='true' />

                <div className='brief-inner'>
                    <div className='brief-copy'>
                        <p className='brief-eyebrow'>
                            <img className='brief-eyebrow-icon' src='/images/ui/eyebrow-triangle.svg' alt='' width={5} height={10} />
                            <span className='brief-eyebrow-text'>start a project</span>
                        </p>
                        <h1 className='brief-title'>Tell us what you're shipping.</h1>
                        <p className='brief-sub'>
                            Brief us on your project. We'll respond within 24 hours. If it's a fit, we'll set up a
                            30-minute kickoff call this week.
                        </p>
                        <dl className='brief-facts'>
                            <div className='brief-fact'>
                                <dt className='brief-fact-key'>Reply time</dt>
                                <dd className='brief-fact-value'>Within 24 hours, Mon to Fri</dd>
                            </div>
                            <div className='brief-fact'>
                                <dt className='brief-fact-key'>Budgets</dt>
                                <dd className='brief-fact-value'>Typically $5k to $100k+, smaller if it's a fit</dd>
                            </div>
                            <div className='brief-fact'>
                                <dt className='brief-fact-key'>Who replies</dt>
                                <dd className='brief-fact-value'>The people who do the work</dd>
                            </div>
                        </dl>
                    </div>

                </div>
            </section>

            <div className='tgrid-dark' aria-hidden='true'><div className='tgrid-dark-rail' /></div>

            <section className='ladder' data-ground='light'>
                <div className='ladder-rail' aria-hidden='true' />
                <div className='ladder-inner'>
                    <h2 className='ladder-title'>From brief to live.</h2>
                    <ol className='ladder-steps'>
                        {STEPS.map((step, index) => (
                            <li key={step.title} className='ladder-step'>
                                <p className='ladder-step-num'>{String(index + 1).padStart(2, '0')}</p>
                                <h3 className='ladder-step-title'>{step.title}</h3>
                                <p className='ladder-step-text'>{step.text}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>
        </Layout>
    )
}

export default Contact
